<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">

    <!-- NS Form Card -->
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-1 flex items-center justify-between gap-3">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Non-Stockable Purchase (NS)</h2>
        <div class="relative print-hide shrink-0" ref="noReceiptDropdownRef">
          <button
            type="button"
            @click.stop="toggleNoReceiptDropdown"
            class="px-3 py-1.5 text-[0.78rem] font-semibold text-[#0d4169] border border-[#cbd5e1] rounded-md bg-white hover:bg-slate-50 transition-all whitespace-nowrap"
          >
            Has No Reciept NSs
            <span v-if="noReceiptNsList.length" class="ml-1 text-slate-500">({{ noReceiptNsList.length }})</span>
          </button>
          <div
            v-if="noReceiptDropdownOpen"
            class="absolute right-0 top-full mt-1 z-50 w-72 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
            @click.stop
          >
            <div class="px-3 py-2 border-b border-slate-100 text-[0.72rem] font-bold text-slate-600 uppercase tracking-wide">
              NS Without Receipt
            </div>
            <div v-if="loadingNoReceiptNs" class="px-3 py-4 text-center text-sm text-slate-500">
              Loading...
            </div>
            <div v-else-if="!noReceiptNsList.length" class="px-3 py-4 text-center text-sm text-slate-400 italic">
              No NS without receipt
            </div>
            <div v-else class="max-h-60 overflow-y-auto">
              <label
                v-for="row in noReceiptNsList"
                :key="row.nsNo"
                class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-b-0"
              >
                <input
                  v-model="selectedNoReceiptNs"
                  type="checkbox"
                  :value="row.nsNo"
                  class="w-4 h-4 accent-[#0d4169] cursor-pointer shrink-0"
                >
                <span class="text-sm font-semibold text-slate-700">{{ row.nsNo }}</span>
              </label>
            </div>
            <div class="border-t border-slate-100 px-3 py-2 flex justify-end">
              <button
                type="button"
                @click="saveNoReceiptNs"
                :disabled="savingNoReceiptNs || !selectedNoReceiptNs.length"
                class="px-4 py-1.5 bg-[#0d4169] text-white text-xs font-bold rounded-md hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ savingNoReceiptNs ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <form @submit.prevent="processNs" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">

          <!-- NS Number (display-only) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">NS Number</label>
            <input
              :value="nsNumberDisplay || '-'"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            />
          </div>

          <!-- Transaction Date -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <input v-model="form.date" type="date"
              :min="isPendingEditMode ? null : todayDate"
              :max="todayDate"
              :disabled="isPendingEditMode && !canEditTransactionDate"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required />
          </div>

          <!-- PR Number -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">PR Number</label>
            <input v-model="form.prNo" type="text" placeholder="e.g. PR-20240001"
              :disabled="isPendingEditMode && !canEditPrNo"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <!-- Supplier -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Supplier</label>
            <select v-model="form.supplierId"
              :disabled="isSupplierLocked || (isPendingEditMode && !canEditSupplier)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed" required>
              <option value="" disabled>Select Supplier</option>
              <option v-for="s in visibleSuppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>

          <!-- Department -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Department</label>
            <select
              v-model="form.departmentId"
              :disabled="isDepartmentLocked || (isPendingEditMode && !canEditDepartment)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              required
            >
              <option value="" disabled>Select Department</option>
              <option v-for="d in visibleDepartments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Category</label>
            <select v-model="form.categoryId"
              :disabled="isPendingEditMode && !canEditCategory"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
              <option value="" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <!-- Item Name (items + products merged, products in green) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item Name</label>
            <select v-model="form.selectedItemKey"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              :disabled="!form.categoryId || (isPendingEditMode && !canEditItemField)" required>
              <option value="" disabled>Select Item</option>
              <option
                v-for="opt in filteredSelectableItems"
                :key="opt.key"
                :value="opt.key"
                :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
              >
                {{ opt.name }} ({{ opt.unit || '-' }})
              </option>
            </select>
          </div>

          <!-- Is Taxable -->
          <div class="flex items-center gap-4 pt-5 flex-wrap">
            <div class="flex items-center gap-2">
              <input id="isTaxable" v-model="form.isTaxable" :disabled="isPendingEditMode && !canEditTaxable" type="checkbox" class="w-4 h-4 accent-[#0d4169] cursor-pointer">
              <label for="isTaxable" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer">Is Taxable Item?</label>
            </div>
          </div>

          <!-- Item Specifications -->
          <div class="col-span-2 md:col-span-3 lg:col-span-2 w-full min-w-0 flex flex-col gap-1">
            <div class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Item Specifications</div>
            <div class="w-full min-w-0" style="border: 1px solid #cbd5e1; border-radius: 6px; padding: 2px; background-color: #fdfdfd; position: relative;" >
              <div class="flex flex-col gap-1">
                <div v-for="(entry, idx) in variantEntries" :key="idx" class="relative grid grid-cols-2 gap-1 sm:grid-cols-3 border border-slate-100 rounded-lg shadow-sm">
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Qty</label>
                    <input    step="any"
                    inputmode="decimal" v-model.number="entry.quantity" :disabled="isPendingEditMode && !canEditQtyAt(idx)" type="number" min="0" placeholder="0" class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs" required />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Unit Price</label>
                    <input    step="any"
                    inputmode="decimal" v-model.number="entry.unitPrice" :disabled="isPendingEditMode" type="number" min="0" placeholder="0.00" class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs" required />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label v-if="idx === 0" class="text-[0.7rem] font-semibold text-slate-500">Volume <span v-if="selectedUnit" class="text-red-500">({{ selectedUnit }})</span></label>
                    <input v-model="entry.variant" :disabled="isPendingEditMode && !canEditVolumeAt(idx)" type="number" min="0" placeholder="0" class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs" required />
                  </div>
                  <button v-if="variantEntries.length > 1 && !isPendingEditMode" type="button" @click="removeVariantEntry(idx)" class="absolute -top-1.5 -right-1.5 h-4 w-4 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] shadow-md border border-white hover:bg-red-600 transition-colors"> - </button>
                </div>
              </div>
              <button v-if="!isPendingEditMode" type="button" @click="addVariantEntry" class="mr-1 absolute -bottom-1.5 -right-1.5 h-4 w-4 flex items-center justify-center bg-green-600 text-white rounded-full text-[10px] font-bold shadow-lg border border-white hover:bg-green-700 transition-transform active:scale-90 z-10"> + </button>
            </div>
          </div>

          <!-- Total Price -->
          <div class="flex flex-col gap-1 mt-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Total Price</label>
            <input :value="totalPrice.toFixed(2)" type="text" readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed">
          </div>

          <!-- Remark -->
          <div class="flex flex-col gap-1 md:col-span-2 lg:col-span-1 ">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Remark</label>
            <input v-model="form.remark" type="text" placeholder="Item specific notes..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-end items-center gap-3">
          <div v-if="isPendingEditMode" class="flex items-center gap-2 mr-auto">
            <input id="batchHasReceiptPending" v-model="batchHasReceipt" type="checkbox" class="w-4 h-4 accent-[#0d4169] cursor-pointer">
            <label for="batchHasReceiptPending" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer whitespace-nowrap">Has Reciept?</label>
          </div>
          <button v-if="isEditing" type="button" @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            {{ isEditing ? 'Update NS' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Pending Edit Access (NS) -->
    <div v-if="pendingFlattenedNsList.length > 0" class="flex flex-col gap-2">
      <div class="print-hide w-full flex justify-between items-center mb-2 px-2">
        <h3 class="text-[0.8rem] font-bold text-slate-600">Pending Edit Access (NS)</h3>
      </div>
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th>#</th>
              <th>NS No</th>
              <th>PR No</th>
              <th>Supplier</th>
              <th>Category</th>
              <th>Dept</th>
              <th>Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in pendingFlattenedNsList" :key="`pending-${index}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ row.nsNo }}</td>
              <td>{{ row.prNo || '-' }}</td>
              <td>{{ row.supplierName }}</td>
              <td>{{ row.categoryName }}</td>
              <td>{{ row.departmentName }}</td>
              <td class="font-semibold" :class="row.itemType === 'product' ? 'text-green-600' : 'text-[#0d4169]'">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayVariant }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ row.displayTotalPrice }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <span class="status-pill text-[0.6rem]" :class="row.isTaxable ? 'active' : 'inactive'">
                  {{ row.isTaxable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-500 italic">{{ row.remark || '-' }}</td>
              <td v-if="row.isPrimaryRow" :rowspan="row.groupSize" class="px-2 py-1.5 text-[0.7rem] text-slate-700 print-hide bg-white">
                <div class="flex items-center gap-1">
                  <button @click="loadPendingIntoForm(row.originalIndex)" class="editbtn hover:underline text-emerald-600">Edit</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pending NS Receive Approvals -->
    <div v-if="pendingReceiveNsItems.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Pending NS Receive Approvals</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>NS No</th>
              <th>PR No</th>
              <th>Supplier</th>
              <th>Category</th>
              <th>Dept</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Qty</th>
              <th>Volume</th>
              <th>Taxable</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in pendingReceiveNsItems" :key="`${r.id}-${idx}`">
              <td>{{ idx + 1 }}</td>
              <td>{{ r.nsNo }}</td>
              <td>{{ r.prNo }}</td>
              <td>{{ r.supplier }}</td>
              <td>{{ r.category }}</td>
              <td>{{ r.department }}</td>
              <td class="font-semibold text-[#0d4169]">{{ r.item }}</td>
              <td>{{ r.uom || '-' }}</td>
              <td class="font-bold">{{ r.qty }}</td>
              <td>{{ r.volume }}</td>
              <td>
                <span class="status-pill text-[0.6rem]" :class="r.taxable ? 'active' : 'inactive'">
                  {{ r.taxable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td>
                <button
                  @click="approvePendingReceiveNs(r.id)"
                  :disabled="approvingReceiveNsIds.has(r.id)"
                  class="px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded-md hover:bg-emerald-600 transition-all shadow-sm active:scale-95 disabled:opacity-50"
                >
                  {{ approvingReceiveNsIds.has(r.id) ? 'Approving...' : 'Approve' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- NS List Table -->
    <div v-if="nsList.length > 0" class="flex flex-col gap-2">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        
        <!-- Print Header -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">Non-Stockable Purchase (NS)</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th>#</th>
              <th>NS No</th>
              <th>PR No</th>
              <th>Supplier</th>
              <th>Category</th>
              <th>Dept</th>
              <th>Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in flattenedNsList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ row.nsNo }}</td>
              <td>{{ row.prNo || '-' }}</td>
              <td>{{ row.supplierName }}</td>
              <td>{{ row.categoryName }}</td>
              <td>{{ row.departmentName }}</td>
              <td class="font-semibold" :class="row.itemType === 'product' ? 'text-green-600' : 'text-[#0d4169]'">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayVariant }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ row.displayTotalPrice }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <span class="status-pill text-[0.6rem]" :class="row.isTaxable ? 'active' : 'inactive'">
                  {{ row.isTaxable ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-500 italic">{{ row.remark || '-' }}</td>
              <td v-if="row.isPrimaryRow" :rowspan="row.groupSize" class="px-2 py-1.5 text-[0.7rem] text-slate-700 print-hide bg-white">
                <div class="flex items-center gap-1">
                  <button @click="editItem(row.originalIndex)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button @click="deleteItem(row.originalIndex)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">Delete</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
          <div class="flex flex-col gap-1.5 pl-8">
            <div class="flex items-baseline gap-2 text-sm">
              <span>Base Amount:</span>
              <span class="font-bold">{{ baseTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2 text-sm">
              <span>Taxable Amount:</span>
              <span class="font-bold">{{ taxableAmountTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2 text-sm">
              <span>Tax (15%):</span>
              <span class="font-bold">{{ taxTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
              <span class="font-black text-[#0d4169]">Grand Total:</span>
              <span class="font-black text-base text-[#0d4169]">{{ grandTotal.toFixed(2) }}</span>
            </div>
          </div>

          <div class="mt-16 flex justify-between px-10 text-sm">
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Prepared By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Checked By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Authorized By</strong>
            </div>
          </div>
        </div>

        <!-- Summary & Save Button -->
        <div class="print-hide flex justify-between items-end px-4 mt-6 mb-10">
          <div class="flex flex-col gap-2">
            <div class="flex items-baseline gap-2">
              <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Base Amount:</span>
              <span class="text-sm font-bold text-slate-800">{{ baseTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2">
              <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Taxable Amount:</span>
              <span class="text-sm font-bold text-slate-800">{{ taxableAmountTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2">
              <span class="text-[0.7rem] text-slate-500 font-bold uppercase tracking-wider">Tax (15%):</span>
              <span class="text-sm font-bold text-slate-800">{{ taxTotal.toFixed(2) }}</span>
            </div>
            <div class="flex items-baseline gap-2 pt-1.5 border-t border-slate-200 mt-1">
              <span class="text-[0.7rem] text-[#0d4169] font-black uppercase tracking-wider leading-none">Grand Total:</span>
              <span class="text-lg font-black text-[#0d4169] leading-none">{{ grandTotal.toFixed(2) }}</span>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <input id="batchHasReceipt" v-model="batchHasReceipt" type="checkbox" class="w-4 h-4 accent-[#0d4169] cursor-pointer">
              <label for="batchHasReceipt" class="text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer whitespace-nowrap">Has Reciept?</label>
            </div>
            <button @click="saveToDatabase" :disabled="isSaving"
              class="min-w-[100px] px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 active:scale-95 disabled:opacity-50 flex justify-center items-center gap-2">
              <div
    v-if="isSaving"
    class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
  ></div>
              <span v-if="!isSaving">Save NS</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

// Dropdowns
const suppliers = ref([])
const departments = ref([])
const categories = ref([])
const registeredItems = ref([])
const products = ref([])

const loading = ref(false)
const isSaving = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)
const editSource = ref(null) // 'items' | 'pending' | null
const pendingEditPermissions = ref(null)
const pendingEditId = ref(null)
const pendingEditIndex = ref(-1)

// Display-only NS number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const nsNumberDisplay = ref('')
const batchHasReceipt = ref(false)

// Has No Receipt NS dropdown
const noReceiptDropdownRef = ref(null)
const noReceiptDropdownOpen = ref(false)
const noReceiptNsList = ref([])
const selectedNoReceiptNs = ref([])
const loadingNoReceiptNs = ref(false)
const savingNoReceiptNs = ref(false)

// Form — uses selectedItemKey (like grvPage) instead of raw itemId
const getInitialForm = () => ({
  nsNo: '',
  date: new Date().toISOString().slice(0, 10),
  prNo: '',
  departmentId: '',
  supplierId: '',
  categoryId: '',
  selectedItemKey: '', // e.g. "item:5" or "product:3"
  itemType: 'item',
  itemId: null,
  productId: null,
  isTaxable: true,
  remark: '',
})
const form = ref(getInitialForm())
const variantEntries = ref([{ quantity: null, unitPrice: null, variant: '' }])

function vatRateForTaxable(isTaxable) {
  return isTaxable ? 15 : 0
}

// Context
const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

function parseAssignedCategoryIds(raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map(v => Number(String(v).trim()))
    .filter(v => Number.isInteger(v) && v > 0)
}

function filterCategoriesByUserAssignment(list) {
  const cats = Array.isArray(list) ? list : []
  const user = currentUser.value
  let privilege = {}
  const rawPriv = user?.privilege
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch {
      privilege = {}
    }
  }
  const hasAll = privilege?.All === true || privilege?.all === true
  const assignedIds = parseAssignedCategoryIds(user?.category_ids || user?.categoryIds)

  if (hasAll && assignedIds.length === 0) {
    return cats
  }
  if (assignedIds.length === 0) {
    return cats
  }
  const idSet = new Set(assignedIds)
  return cats.filter(c => idSet.has(Number(c.id)))
}

// Select Category options: All:true + empty category_ids → all; otherwise assigned category_ids.
const selectableCategories = computed(() => filterCategoriesByUserAssignment(categories.value))

const todayDate = new Date().toISOString().split('T')[0]

// Computed
const totalPrice = computed(() =>
  variantEntries.value.reduce((sum, e) => sum + (Number(e.quantity || 0) * Number(e.unitPrice || 0)), 0)
)

// Merged item+product list filtered by category (products in green via style in template)
const filteredSelectableItems = computed(() => {
  if (!form.value.categoryId) return []
  const catId = Number(form.value.categoryId)

  const itemOptions = registeredItems.value
    .filter(item => Number(item.categoryId) === catId)
    .map(item => ({
      key: `item:${item.id}`,
      type: 'item',
      id: item.id,
      name: item.itemName,
      unit: item.unit,
      unitProduction: item.unitProduction,
    }))

  const activeProducts = products.value.filter(p => (p.status ?? 1) === 1)
  const productOptions = activeProducts
    .filter(p => {
      const pCat = p.categoryId ?? p.category_id ?? p.category?.id ?? null
      return pCat == null ? true : Number(pCat) === catId
    })
    .map(p => ({
      key: `product:${p.id}`,
      type: 'product',
      id: p.id,
      name: p.name,
      unit: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? '',
      unitProduction: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? '',
    }))

  return [...itemOptions, ...productOptions].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  )
})

// Resolve selected item/product from key
const selectedItemObj = computed(() => {
  const key = String(form.value.selectedItemKey || '')
  if (!key) return null
  const [type, idStr] = key.split(':')
  const id = Number(idStr)
  if (type === 'product') {
    const p = products.value.find(x => Number(x.id) === id)
    if (!p) return null
    return { type: 'product', id: p.id, name: p.name, unit: p.uom ?? p.UOM ?? p.unit ?? p.unitProduction ?? '' }
  }
  const it = registeredItems.value.find(x => Number(x.id) === id)
  if (!it) return null
  return { type: 'item', id: it.id, name: it.itemName, unit: it.unit, unitProduction: it.unitProduction }
})

const selectedUnit = computed(() => {
  const sel = selectedItemObj.value
  if (!sel) return ''
  if (sel.type === 'product') {
    const u = String(sel.unitProduction || sel.unit || '').trim()
    if (u === 'K.G.') return 'Gram'
    if (u === 'liter') return 'M.L.'
    if (u === 'Pieces') return 'Pieces'
    return u
  }

  if(sel.unitProduction === 'Glass' && sel.unit === 'K.G.') {
  return 'Gramm'
}

if(sel.unitProduction === 'Glass' && sel.unit === 'liter') {
  return 'M.L.'
}
  return sel.unitProduction || ''
})

const nsList = ref([])

// Lock Supplier/Department while there are temp NS rows.
// If nsList becomes empty, these become editable again.
const supplierLockId = ref(null)
const departmentLockId = ref(null)
const isSupplierLocked = computed(() => nsList.value.length > 0)
const isDepartmentLocked = computed(() => nsList.value.length > 0)
const visibleSuppliers = computed(() => {
  if (!isSupplierLocked.value || supplierLockId.value == null) return suppliers.value
  return suppliers.value.filter(s => Number(s.id) === Number(supplierLockId.value))
})
const visibleDepartments = computed(() => {
  if (!isDepartmentLocked.value || departmentLockId.value == null) return departments.value
  return departments.value.filter(d => Number(d.id) === Number(departmentLockId.value))
})

watch(
  () => nsList.value,
  (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      supplierLockId.value = null
      departmentLockId.value = null
      return
    }

    if (supplierLockId.value == null) {
      const first = list[0]
      const id = first?.supplierId ?? first?.supplier_id ?? null
      supplierLockId.value = id == null ? null : Number(id)
    }

    if (departmentLockId.value == null) {
      const first = list[0]
      const id = first?.departmentId ?? first?.department_id ?? null
      departmentLockId.value = id == null ? null : Number(id)
    }

    if (supplierLockId.value != null && String(form.value.supplierId) !== String(supplierLockId.value)) {
      form.value.supplierId = supplierLockId.value
    }

    if (departmentLockId.value != null && String(form.value.departmentId) !== String(departmentLockId.value)) {
      form.value.departmentId = departmentLockId.value
    }
  },
  { immediate: true }
)

const baseTotal = computed(() => nsList.value.reduce((sum, item) => sum + (item.totalPrice || 0), 0))
const taxableAmountTotal = computed(() => nsList.value.reduce((sum, item) => item.isTaxable ? sum + (item.totalPrice || 0) : sum, 0))
const taxTotal = computed(() => nsList.value.reduce((sum, item) => item.isTaxable ? sum + (item.totalPrice * 0.15) : sum, 0))
const grandTotal = computed(() => baseTotal.value + taxTotal.value)
const pendingNsList = ref([])
const pendingReceiveNsItems = ref([])
const approvingReceiveNsIds = ref(new Set())
const isPendingEditMode = computed(() => editSource.value === 'pending')
const canEditTransactionDate = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.transactionDate)
const canEditPrNo = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.prNo)
const canEditSupplier = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.supplier)
const canEditDepartment = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.department)
const canEditCategory = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.category)
const canEditItemField = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.itemField)
const canEditTaxable = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.taxable)

function splitCsvBool(s) {
  if (s == null) return []
  return String(s)
    .split(',')
    .map(x => String(x).trim())
    .filter(x => x !== '')
    .map(v => v === 'true' || v === '1')
}

function canEditByDetailIndex(detailFlags, fallback, idx) {
  if (!isPendingEditMode.value) return true
  if (Array.isArray(detailFlags) && detailFlags.length > 0) {
    if (detailFlags[idx] !== undefined) return !!detailFlags[idx]
    return !!detailFlags[detailFlags.length - 1]
  }
  return !!fallback
}

function canEditQtyAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.quantityDetails,
    pendingEditPermissions.value?.qty,
    idx
  )
}

function canEditVolumeAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.volumeDetails,
    pendingEditPermissions.value?.volume,
    idx
  )
}

const flattenedNsList = computed(() => {
  const list = []
  nsList.value.forEach((ns, index) => {
    const qs = (ns.quantityDetails || String(ns.quantity || '')).split(',').filter(s => s !== '')
    const ps = (ns.unitPriceDetails || String(ns.unitPrice || '')).split(',').filter(s => s !== '')
    const vs = (ns.variantDetails || String(ns.variant || '')).split(',').filter(s => s !== '')
    const maxLen = Math.max(1, qs.length, ps.length, vs.length)

    for (let i = 0; i < maxLen; i++) {
      const qty = qs[i] || '0'
      const price = ps[i] || '0'
      list.push({
        ...ns,
        originalIndex: index,
        isPrimaryRow: i === 0,
        displayQuantity: qty,
        displayUnitPrice: price,
        displayVariant: vs[i] || '-',
        displayTotalPrice: (Number(qty) * Number(price)).toFixed(2),
        groupSize: maxLen,
        supplierName: getSupplierName(ns.supplierId),
        categoryName: getCategoryName(ns.categoryId),
        departmentName: getDepartmentName(ns.departmentId),
        itemName: getSelectableName(ns.itemType, ns.itemType === 'product' ? ns.productId : ns.itemId),
        itemType: ns.itemType,
        remark: ns.remark
      })
    }
  })
  return list
})

const pendingFlattenedNsList = computed(() => {
  const list = []
  pendingNsList.value.forEach((ns, originalIndex) => {
    const qs = (ns.quantityDetails || String(ns.quantity || '')).split(',').filter(s => s !== '')
    const ps = (ns.unitPriceDetails || String(ns.unitPrice || '')).split(',').filter(s => s !== '')
    const vs = (ns.variantDetails || String(ns.variant || '')).split(',').filter(s => s !== '')
    const maxLen = Math.max(1, qs.length, ps.length, vs.length)

    for (let i = 0; i < maxLen; i++) {
      const qty = qs[i] || '0'
      const price = ps[i] || '0'
      list.push({
        ...ns,
        originalIndex,
        isPrimaryRow: i === 0,
        groupSize: maxLen,
        displayQuantity: qty,
        displayUnitPrice: price,
        displayVariant: vs[i] || '-',
        displayTotalPrice: (Number(qty) * Number(price)).toFixed(2),
        supplierName: getSupplierName(ns.supplierId),
        categoryName: getCategoryName(ns.categoryId),
        departmentName: getDepartmentName(ns.departmentId),
        itemName: getSelectableName(ns.itemType, ns.itemType === 'product' ? ns.productId : ns.itemId),
        itemType: ns.itemType,
        remark: ns.remark
      })
    }
  })
  return list
})

function loadPendingIntoForm(index) {
  const record = pendingNsList.value[index]
  if (!record) return
  batchHasReceipt.value = !!record?.hasReceipt
  form.value = {
    ...record,
    selectedItemKey: record.itemType === 'product'
      ? `product:${record.productId}`
      : `item:${record.itemId}`
  }
  const qs = (record.quantityDetails || '').split(',')
  const ps = (record.unitPriceDetails || '').split(',')
  const vs = (record.variantDetails || '').split(',')
  variantEntries.value = qs.map((q, i) => ({ quantity: parseFloat(q), unitPrice: parseFloat(ps[i]), variant: vs[i] }))
  // Pending mode: lock fields based on ns_edit_access true/false flags.
  isEditing.value = true
  editIndex.value = -1
  editSource.value = 'pending'
  pendingEditPermissions.value = record?._permissions || null
  pendingEditId.value = record?.id ?? null
  pendingEditIndex.value = index
}

function toBool(v) {
  return v === true || String(v).toLowerCase() === 'true' || Number(v) === 1
}

async function loadPendingNsEditAccessRows() {
  pendingNsList.value = []
  try {
    const pendingRes = await apiFetch('/edit-access/ns?status=Pending', { method: 'GET' })
    if (!pendingRes.ok) return
    const pending = await pendingRes.json().catch(() => [])
    if (!Array.isArray(pending) || pending.length === 0) return

    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const nsRes = await apiFetch(`/non-stockable?${query}`, { method: 'GET' })
    if (!nsRes.ok) return
    const allNs = await nsRes.json().catch(() => [])
    if (!Array.isArray(allNs) || allNs.length === 0) return

    const nsItemById = new Map()
    for (const n of allNs) {
      const items = Array.isArray(n?.items) ? n.items : []
      for (const ni of items) {
        const id = Number(ni?.id)
        if (!Number.isFinite(id)) continue
        nsItemById.set(id, { ns: n, nsItem: ni })
      }
    }

    const records = []
    for (const req of pending) {
      const matched = nsItemById.get(Number(req?.nsId))
      if (!matched) continue
      const { ns, nsItem } = matched

      const nsCreatorId = Number(ns?.created_by ?? ns?.createdBy ?? ns?.creator?.id ?? 0)
      if (Number(userId.value || 0) !== nsCreatorId) continue

      const requestedItemId = Number(req?.item)
      const nsItemItemId = Number(nsItem?.itemId ?? nsItem?.item_id ?? 0)
      const nsItemProductId = Number(nsItem?.productId ?? nsItem?.product_id ?? 0)
      if (Number.isFinite(requestedItemId) && requestedItemId > 0) {
        const matchesItem = nsItemItemId > 0 && nsItemItemId === requestedItemId
        const matchesProduct = nsItemProductId > 0 && nsItemProductId === requestedItemId
        if (!matchesItem && !matchesProduct) continue
      }

      const quantityDetails = String(nsItem?.quantityDetails ?? nsItem?.quantity ?? '')
      const unitPriceDetails = String(nsItem?.unitPriceDetails ?? nsItem?.unitPrice ?? '')
      const variantDetails = String(nsItem?.variantDetails ?? '')
      const quantities = quantityDetails.split(',').map(v => Number(v || 0))
      const totalQty = quantities.reduce((sum, v) => sum + Number(v || 0), 0)
      const firstUnitPrice = Number((unitPriceDetails.split(',')[0] || 0))
      const lineTotal = quantityDetails.split(',').reduce((sum, qRaw, idx) => {
        const q = Number(qRaw || 0)
        const p = Number(unitPriceDetails.split(',')[idx] ?? unitPriceDetails.split(',')[0] ?? 0)
        return sum + (q * p)
      }, 0)

      records.push({
        id: req?.id ?? null,
        nsItemId: Number(req?.nsId) || null,
        nsNo: ns?.nsNo || req?.nsNo || '-',
        date: ns?.date ? new Date(ns.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        prNo: ns?.prNo || '',
        supplierId: ns?.supplierId ?? '',
        departmentId: ns?.departmentId ?? '',
        categoryId: ns?.categoryId ?? '',
        itemType: nsItemProductId > 0 ? 'product' : 'item',
        itemId: nsItemProductId > 0 ? null : (nsItemItemId || null),
        productId: nsItemProductId > 0 ? nsItemProductId : null,
        quantity: totalQty,
        unitPrice: firstUnitPrice,
        totalPrice: Number(lineTotal || 0),
        quantityDetails,
        unitPriceDetails,
        variantDetails,
        isTaxable: !!nsItem?.isTaxable,
        hasReceipt: !!ns?.hasReceipt,
        vat: nsItem?.vat != null ? Number(nsItem.vat) : vatRateForTaxable(!!nsItem?.isTaxable),
        remark: nsItem?.remark ?? ns?.remark ?? '',
        _permissions: {
          transactionDate: toBool(req?.transactionDate),
          prNo: toBool(req?.prNo),
          supplier: toBool(req?.supplier),
          department: toBool(req?.department),
          category: toBool(req?.category),
          itemField: toBool(req?.itemField),
          qty: toBool(req?.qty),
          volume: toBool(req?.volume),
          taxable: toBool(req?.taxable),
          quantityDetails: splitCsvBool(req?.quantityDetails),
          volumeDetails: splitCsvBool(req?.volumeDetails),
        },
      })
    }

    pendingNsList.value = records
  } catch (err) {
    console.error('Error loading pending NS edit access rows:', err)
  }
}

async function loadPendingReceiveNsItems() {
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/non-stockable/receive/pending?${query}`)
    if (!res.ok) {
      pendingReceiveNsItems.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    pendingReceiveNsItems.value = Array.isArray(data?.data) ? data.data : []
  } catch (err) {
    console.error('Error loading pending NS receive approvals:', err)
    pendingReceiveNsItems.value = []
  }
}

async function approvePendingReceiveNs(id) {
  const itemId = Number(id)
  if (!Number.isFinite(itemId)) return
  if (approvingReceiveNsIds.value.has(itemId)) return
  if (!confirm('Are you sure ......................... Approve?')) return

  const next = new Set(approvingReceiveNsIds.value)
  next.add(itemId)
  approvingReceiveNsIds.value = next

  try {
    const res = await apiFetch(`/non-stockable/receive/${itemId}/approve`, { method: 'POST' })
    if (res.ok) {
      await loadPendingReceiveNsItems()
    } else {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to approve')
    }
  } catch (err) {
    console.error('Error approving NS receive item:', err)
  } finally {
    const after = new Set(approvingReceiveNsIds.value)
    after.delete(itemId)
    approvingReceiveNsIds.value = after
  }
}

// Fetch
async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [supRes, depRes, catRes, itemRes, prodRes] = await Promise.all([
      apiFetch(branchId.value ? `/suppliers/branch/${branchId.value}` : `/suppliers/company/${companyId.value}`),
      apiFetch(`/departments?${query}`),
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/products?${query}&activeOnly=true`),
    ])
    if (supRes.ok) suppliers.value = await supRes.json()
    suppliers.value = suppliers.value.filter(s => s.status === 1);
    suppliers.value.sort((a, b) => a.name.localeCompare(b.name))
    if (depRes.ok) departments.value = await depRes.json()
    departments.value = departments.value.filter(d => d.status === 1);
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
    categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const itemData = await itemRes.json()
      registeredItems.value = Array.isArray(itemData) ? itemData.filter(i => Number(i?.status) === 1) : []
    }
    if (prodRes.ok) {
      const productData = await prodRes.json()
      products.value = Array.isArray(productData) ? productData.filter(p => Number(p?.status) === 1) : []
    }
  } catch (err) { console.error(err) }
  finally { loading.value = false }
}

async function fetchNextNsNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/non-stockable/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    nsNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching NS number for display:', e)
  }
}

// Actions
async function processNs() {
  if (!isPendingEditMode.value) {
    if (!form.value.prNo) return alert('PR Number is required')
    if (!form.value.supplierId) return alert('Supplier is required')
    if (!form.value.departmentId) return alert('Department is required')
    if (!form.value.categoryId) return alert('Category is required')
    if (!form.value.selectedItemKey) return alert('Item is required')
  }

  // Resolve itemId / productId from selectedItemKey
  const [selType, selIdStr] = String(form.value.selectedItemKey || '').split(':')
  const selId = Number(selIdStr)
  form.value.itemType = selType === 'product' ? 'product' : 'item'
  form.value.itemId = form.value.itemType === 'item' ? selId : null
  form.value.productId = form.value.itemType === 'product' ? selId : null

  const cleaned = variantEntries.value.filter(e => e.quantity > 0 && e.unitPrice > 0)
  if (!isPendingEditMode.value && cleaned.length === 0) return alert('Add at least one item specification with quantity and price')

  const record = {
    ...form.value,
    quantity: cleaned.reduce((s, e) => s + (e.quantity || 0), 0),
    unitPrice: cleaned[0]?.unitPrice || 0,
    totalPrice: totalPrice.value,
    quantityDetails: cleaned.map(e => e.quantity).join(','),
    unitPriceDetails: cleaned.map(e => e.unitPrice).join(','),
    variantDetails: cleaned.map(e => e.variant).join(','),
    taxAmount: form.value.isTaxable ? totalPrice.value * 0.15 : 0,
    grandTotal: form.value.isTaxable ? totalPrice.value * 1.15 : totalPrice.value,
    vat: vatRateForTaxable(!!form.value.isTaxable),
  }

  if (isEditing.value) {
    if (editSource.value === 'items' && editIndex.value >= 0) {
      nsList.value[editIndex.value] = record
      localSave()
    } else {
      const targetId = pendingEditId.value
      let idx = -1
      if (targetId != null && targetId !== '') {
        idx = pendingNsList.value.findIndex(r => Number(r?.id) === Number(targetId))
      }
      if (idx === -1 && pendingEditIndex.value >= 0 && pendingEditIndex.value < pendingNsList.value.length) {
        idx = pendingEditIndex.value
      }
      const pendingRecord = idx !== -1 ? pendingNsList.value[idx] : null

      if (!pendingRecord?.id || !pendingRecord?.nsItemId) {
        alert('Pending NS record not found.')
        return
      }

      const res = await apiFetch(`/edit-access/ns/${pendingRecord.id}/complete`, {
        method: 'PATCH',
        body: JSON.stringify({
          nsItemId: pendingRecord.nsItemId,
          update: {
            transactionDate: record.date,
            prNo: record.prNo,
            supplierId: record.supplierId,
            departmentId: record.departmentId,
            categoryId: record.categoryId,
            itemType: record.itemType,
            itemId: record.itemId,
            productId: record.productId,
            quantityDetails: record.quantityDetails,
            unitPriceDetails: record.unitPriceDetails,
            variantDetails: record.variantDetails,
            isTaxable: record.isTaxable,
            hasReceipt: !!batchHasReceipt.value,
            vat: vatRateForTaxable(!!record.isTaxable),
            remark: record.remark,
          }
        })
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        if (res.status === 404) {
          alert('Pending NS update endpoint was not found. Please restart the API server and try again.')
          return
        }
        const details = err?.error ? ` (${err.error})` : ''
        alert(`${err?.message || 'Failed to update pending NS.'}${details}`)
        return
      }

      await loadPendingNsEditAccessRows()
      alert('Pending NS updated successfully.')
    }
    isEditing.value = false
    editIndex.value = -1
    editSource.value = null
    pendingEditPermissions.value = null
    pendingEditId.value = null
    pendingEditIndex.value = -1
  } else {
    nsList.value.unshift(record)
    if (supplierLockId.value == null) {
      const supId = record?.supplierId ?? record?.supplier_id ?? null
      supplierLockId.value = supId == null ? null : Number(supId)
    }
    if (departmentLockId.value == null) {
      const depId = record?.departmentId ?? record?.department_id ?? null
      departmentLockId.value = depId == null ? null : Number(depId)
    }
    localSave()
  }

  const header = { date: form.value.date, prNo: form.value.prNo, supplierId: form.value.supplierId, departmentId: form.value.departmentId }
  form.value = { ...getInitialForm(), ...header }
  variantEntries.value = [{ quantity: null, unitPrice: null, variant: '' }]
}

function editItem(index) {
  const record = nsList.value[index]
  form.value = {
    ...record,
    selectedItemKey: record.itemType === 'product'
      ? `product:${record.productId}`
      : `item:${record.itemId}`
  }
  const qs = (record.quantityDetails || '').split(',')
  const ps = (record.unitPriceDetails || '').split(',')
  const vs = (record.variantDetails || '').split(',')
  variantEntries.value = qs.map((q, i) => ({ quantity: parseFloat(q), unitPrice: parseFloat(ps[i]), variant: vs[i] }))
  isEditing.value = true
  editIndex.value = index
  editSource.value = 'items'
  pendingEditPermissions.value = null
  pendingEditId.value = null
  pendingEditIndex.value = -1
}

function cancelEdit() {
  isEditing.value = false
  editIndex.value = -1
  editSource.value = null
  pendingEditPermissions.value = null
  pendingEditId.value = null
  pendingEditIndex.value = -1
  const header = { date: form.value.date, prNo: form.value.prNo, supplierId: form.value.supplierId, departmentId: form.value.departmentId }
  form.value = { ...getInitialForm(), ...header }
  variantEntries.value = [{ quantity: null, unitPrice: null, variant: '' }]
}

function deleteItem(index) {
  nsList.value.splice(index, 1)
  localSave()
}

async function saveToDatabase() {
  if (!nsList.value.length) return
  isSaving.value = true
  try {
    const first = nsList.value[0]
    const payload = {
      date: first.date,
      prNo: first.prNo,
      supplierId: first.supplierId,
      categoryId: first.categoryId,
      departmentId: first.departmentId,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      totalAmount: Number(grandTotal.value || 0),
      hasReceipt: !!batchHasReceipt.value,
      items: nsList.value.map(i => ({
        itemId: i.itemType === 'product' ? null : i.itemId,
        productId: i.itemType === 'product' ? i.productId : null,
        description: getSelectableName(i.itemType, i.itemType === 'product' ? i.productId : i.itemId),
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        totalPrice: i.totalPrice,
        isTaxable: i.isTaxable,
        vat: i.vat !== undefined && i.vat !== null
          ? Number(i.vat)
          : vatRateForTaxable(!!i.isTaxable),
        taxAmount: i.taxAmount,
        grandTotal: i.grandTotal,
        quantityDetails: i.quantityDetails,
        unitPriceDetails: i.unitPriceDetails,
        variantDetails: i.variantDetails,
        remark: i.remark
      }))
    }
    const res = await apiFetch('/non-stockable', { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedNsNo = responseData?.nsNo || responseData?.data?.nsNo || ''
      if (savedNsNo) {
        nsList.value = nsList.value.map((row) => ({ ...row, nsNo: savedNsNo }))
      }
      alert(savedNsNo ? `NS saved successfully. NS Number: ${savedNsNo}` : 'NS saved successfully.')

      setTimeout(async () => {
        const pdfRowsSnapshot = flattenedNsList.value.map(row => ({ ...row }))
        const baseTotalSnapshot = Number(baseTotal.value || 0)
        const taxableAmountSnapshot = Number(taxableAmountTotal.value || 0)
        const taxTotalSnapshot = Number(taxTotal.value || 0)
        const grandTotalSnapshot = Number(grandTotal.value || 0)

        if (confirm("Are you sure..........Print?")) {
          await generateNsPdf({
            rows: pdfRowsSnapshot,
            baseTotal: baseTotalSnapshot,
            taxableAmount: taxableAmountSnapshot,
            taxTotal: taxTotalSnapshot,
            grandTotal: grandTotalSnapshot
          })
        }
        nsList.value = []
        batchHasReceipt.value = false
        localStorage.removeItem('Mina_ns_temp')
        form.value = { ...getInitialForm() }
        // Refresh next NS number for the next entry.
        await fetchNextNsNumberForDisplay()
      }, 150)
    } else {
      const err = await res.json()
      alert('Error: ' + err.message)
    }
  } catch (err) { console.error(err) }
  finally { isSaving.value = false }
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

async function generateNsPdf(snapshot = null) {
  const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : flattenedNsList.value
  if (!rows.length) return
  const baseAmount = Number(snapshot?.baseTotal ?? baseTotal.value ?? 0)
  const taxableAmount = Number(snapshot?.taxableAmount ?? taxableAmountTotal.value ?? 0)
  const taxAmount = Number(snapshot?.taxTotal ?? taxTotal.value ?? 0)
  const grandAmount = Number(snapshot?.grandTotal ?? grandTotal.value ?? 0)
  const meta = {
    nsNumber: rows[0]?.nsNo || form.value.nsNo || 'NS',
    transactionDate: rows[0]?.date || form.value.date || new Date().toISOString().slice(0, 10)
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 16
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const today = new Date().toLocaleDateString()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  const tableBody = rows.map((row, idx) => ([
    String(idx + 1),
    row.nsNo || '-',
    row.prNo || '-',
    row.supplierName || '-',
    row.categoryName || '-',
    row.departmentName || '-',
    row.itemName || '-',
    String(row.displayQuantity ?? '-'),
    String(row.displayVariant ?? '-'),
    String(row.displayUnitPrice ?? '-'),
    String(row.displayTotalPrice ?? '-'),
    row.isTaxable ? 'Yes' : 'No',
    row.remark || '-'
  ]))

  autoTable(doc, {
    head: [[
      '#', 'NS No', 'PR No', 'Supplier', 'Category', 'Dept', 'Item', 'Qty', 'Volume',
      'Unit Price', 'Total', 'Taxable', 'Remark'
    ]],
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 9.0,
      cellPadding: 1.2,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text('Non-Stockable Purchase (NS)', pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${today}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 32
  const lineGap = 6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Base Amount:', labelX, finalY, { align: 'right' })
  doc.text(Number(baseAmount || 0).toFixed(2), valueX, finalY, { align: 'right' })
  doc.text('Taxable Amount:', labelX, finalY + lineGap, { align: 'right' })
  doc.text(Number(taxableAmount || 0).toFixed(2), valueX, finalY + lineGap, { align: 'right' })
  doc.text('Tax (15%):', labelX, finalY + (lineGap * 2), { align: 'right' })
  doc.text(Number(taxAmount || 0).toFixed(2), valueX, finalY + (lineGap * 2), { align: 'right' })

  doc.setLineWidth(0.2)
  doc.line(labelX - 18, finalY + (lineGap * 2.5), valueX, finalY + (lineGap * 2.5))
  doc.setFont('helvetica', 'bold')
  doc.text('Grand Total:', labelX, finalY + (lineGap * 3.6), { align: 'right' })
  doc.text(Number(grandAmount || 0).toFixed(2), valueX, finalY + (lineGap * 3.6), { align: 'right' })

  const footerY = Math.max(finalY + (lineGap * 7), pageHeight - 28)
  const firstX = leftMargin + 10
  const secondX = pageWidth / 2 - 20
  const thirdX = pageWidth - rightMargin - 56
  const lineW = 40

  doc.setFont('helvetica', 'normal')
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Received By', firstX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Approved By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeNsNo = String(meta.nsNumber || 'NS').replace(/[\\/:*?"<>|]/g, '-').trim()
  const safeDate = String(meta.transactionDate || new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `${safeNsNo}-${safeDate}.pdf`
  doc.save(filename)
}

function localSave() {
  localStorage.setItem('Mina_ns_temp', JSON.stringify({
    items: nsList.value,
    hasReceipt: batchHasReceipt.value,
  }))
}
function localLoad() {
  const saved = localStorage.getItem('Mina_ns_temp')
  if (!saved) return
  try {
    const parsed = JSON.parse(saved)
    if (Array.isArray(parsed)) {
      nsList.value = parsed
      return
    }
    if (parsed && typeof parsed === 'object') {
      nsList.value = Array.isArray(parsed.items) ? parsed.items : []
      batchHasReceipt.value = !!parsed.hasReceipt
    }
  } catch {
    // ignore invalid local backup
  }
}

function addVariantEntry() { variantEntries.value.push({ quantity: null, unitPrice: null, variant: '' }) }
function removeVariantEntry(idx) { if (variantEntries.value.length > 1) variantEntries.value.splice(idx, 1) }

function getDepartmentName(id) { return departments.value.find(d => d.id === id)?.name || '-' }
function getSupplierName(id) { return suppliers.value.find(s => s.id === id)?.name || '-' }
function getCategoryName(id) { return categories.value.find(c => c.id === id)?.name || '-' }
function getSelectableName(type, id) {
  if (type === 'product') return products.value.find(p => Number(p.id) === Number(id))?.name || '-'
  return registeredItems.value.find(i => Number(i.id) === Number(id))?.itemName || '-'
}

function contextQueryString() {
  return branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
}

function toggleNoReceiptDropdown() {
  noReceiptDropdownOpen.value = !noReceiptDropdownOpen.value
  if (noReceiptDropdownOpen.value) loadNoReceiptNs()
}

async function loadNoReceiptNs() {
  if (!companyId.value && !branchId.value) {
    noReceiptNsList.value = []
    return
  }
  loadingNoReceiptNs.value = true
  selectedNoReceiptNs.value = []
  try {
    const res = await apiFetch(`/non-stockable/no-receipt?${contextQueryString()}`)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      noReceiptNsList.value = Array.isArray(data) ? data : []
    } else {
      noReceiptNsList.value = []
    }
  } catch {
    noReceiptNsList.value = []
  } finally {
    loadingNoReceiptNs.value = false
  }
}

async function saveNoReceiptNs() {
  if (!selectedNoReceiptNs.value.length) {
    alert('Select at least one NS.')
    return
  }
  savingNoReceiptNs.value = true
  try {
    const res = await apiFetch(`/non-stockable/mark-receipt?${contextQueryString()}`, {
      method: 'PATCH',
      body: JSON.stringify({ nsNumbers: selectedNoReceiptNs.value })
    })
    if (res.ok) {
      const data = await res.json().catch(() => ({}))
      alert(`Updated ${data?.count ?? selectedNoReceiptNs.value.length} NS(s) successfully.`)
      noReceiptDropdownOpen.value = false
      selectedNoReceiptNs.value = []
      await loadNoReceiptNs()
    } else {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to update NS receipt status.')
    }
  } catch {
    alert('Failed to update NS receipt status.')
  } finally {
    savingNoReceiptNs.value = false
  }
}

function onNoReceiptDocumentClick(e) {
  if (!noReceiptDropdownOpen.value) return
  if (noReceiptDropdownRef.value && !noReceiptDropdownRef.value.contains(e.target)) {
    noReceiptDropdownOpen.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', onNoReceiptDocumentClick)
  await loadData()
  localLoad()
  await loadPendingNsEditAccessRows()
  await loadPendingReceiveNsItems()
  await fetchNextNsNumberForDisplay()
})

onUnmounted(() => {
  document.removeEventListener('click', onNoReceiptDocumentClick)
})
</script>

<style scoped>
table { font-family: 'Segoe UI', sans-serif; font-size: 12px; color: #1e293b; width: 100%; border-collapse: collapse; }
th, td { padding: 3px 4px; text-align: left; border-bottom: 1px solid #e2e8f0; font-size: 12px; white-space: nowrap; }
th { background: #f1f5f9; cursor: default; font-weight: 600; font-size: 12px !important; color: inherit !important; }
tbody tr:hover { background: #f1f5f9; }
.status-pill { padding: 2px 6px; border-radius: 12px; font-size: 0.65rem; font-weight: bold; display: inline-block; }
.active { background: #dcfce7; color: #166534; }
.inactive { background: #f1f5f9; color: red; }

input:disabled,
select:disabled {
  cursor: not-allowed;
  background-color: #f8fafc;
}

input:disabled:hover,
select:disabled:hover {
  cursor: not-allowed;
  border-color: #ef4444;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    text-align: left !important;
    color: #000 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
